import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import "react-toastify/dist/ReactToastify.css";
import Switch from 'react-switch'
import AdminLayout from "../../layouts/AdminLayout";
import { useFetchAuthorisation, useFetchUserData } from "../../utils/AppFunctions";
import FileUploadField from "../../components/FileUploadField";
import config from "../../app-config";
import { fetch_library_item_by_id, update_library_item_by_id, create_library_item, delete_library_item_by_id } from '../../api/libraryAPI'
import notify from "../../utils/IANotification";
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { MdArrowBack, MdSave, MdDelete } from "react-icons/md";

export default function LibraryFormPage() {
  const navigate = useNavigate();
  useFetchAuthorisation("user");
  useFetchUserData();

  const [activeTab, setActiveTab] = useState("general");
  const [libraryItemData, setLibraryItemData] = useState({
    isbn: "",
    title: "",
    subtitle: "",
    description: "",
    author: "",
    publisher: "",
    media_type: "ebook",
    limit_borrowing: "",
    media_url: "",
    file_id: "",
    is_published: 0,
    is_loanable: 0,
  })

  const libray_id = new URLSearchParams(window.location.search).get("library_id");

  const tabs = [
    { id: 'general', label: 'Item Overview' },
    { id: 'publishing', label: 'Publishing & Media' },
    { id: 'borrowing', label: 'Borrowing Settings' },
  ]

  useEffect(() => {
    fetchLibraryItemById()
  }, [])

  const fetchLibraryItemById = async () => {
    if (!libray_id || libray_id === "new") return
    const { data, error, message, status } = await fetch_library_item_by_id(libray_id)
    if (error || status !== 200) {
      notify({ message: message || error, type: 'error' })
    } else {
      setLibraryItemData(data || {})
    }
  }

  const saveLibraryItem = async () => {
    if (!libray_id || libray_id === "new") return
    const { error, message, status } = await update_library_item_by_id(libray_id, libraryItemData)
    if (error || status !== 200) {
      notify({ message: message || error, type: 'error' })
      return
    }
    notify({ message: 'Library item saved successfully', type: 'success' })
    fetchLibraryItemById()
  }

  const createLibraryItem = async () => {
    const { data, error, message, status } = await create_library_item(libraryItemData)
    if (error || status !== 200) {
      notify({ message: message || error, type: 'error' })
      return
    }
    notify({ message: 'Library item created successfully', type: 'success' })
    navigate('/admin/library')
  }

  const deleteLibraryItem = async () => {
    if (!libray_id || libray_id === "new") return
    const { error, message, status } = await delete_library_item_by_id(libray_id)
    if (error || status !== 200) {
      notify({ message: message || error, type: 'error' })
      return
    }
    notify({ message: 'Library item deleted successfully', type: 'success' })
    navigate('/admin/library')
  }

  return (
    <AdminLayout
      navSelected={"Library Items"}
      headerText={libray_id === "new" ? "New Digital Library Item" : "Edit Library Item"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/library')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {libray_id === "new" ? "Create Library Item" : libraryItemData.title || "Edit Item"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {libray_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteLibraryItem}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (libray_id === "new") createLibraryItem()
              else saveLibraryItem()
            }}>
              {libray_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: ITEM OVERVIEW */}
      {activeTab === "general" && (
        <Card title="Book Overview &amp; Description" subtitle="ISBN identifier, main title, subtitle, and synopsis.">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">ISBN Number</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g. 978-3-16-148410-0"
                  value={libraryItemData.isbn || ""}
                  onChange={(e) => setLibraryItemData({ ...libraryItemData, isbn: e.target.value })}
                />
              </div>

              <div className="input-container md:col-span-2">
                <label className="input-label font-semibold">Book Title</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g. Discipleship in the Local Church"
                  value={libraryItemData.title || ""}
                  onChange={(e) => setLibraryItemData({ ...libraryItemData, title: e.target.value })}
                />
              </div>
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Subtitle / Edition</label>
              <input
                type="text"
                className="text-input"
                placeholder="e.g. A Practical Guide for Leaders"
                value={libraryItemData.subtitle || ""}
                onChange={(e) => setLibraryItemData({ ...libraryItemData, subtitle: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Synopsis &amp; Description</label>
              <textarea
                className="textarea-input h-36"
                placeholder="Overview of the book contents..."
                value={libraryItemData.description || ""}
                onChange={(e) => setLibraryItemData({ ...libraryItemData, description: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: PUBLISHING & MEDIA */}
      {activeTab === "publishing" && (
        <div className="space-y-6">
          <Card title="Publishing Metadata" subtitle="Author and publisher details.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Author</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g. John Stott"
                  value={libraryItemData.author || ""}
                  onChange={(e) => setLibraryItemData({ ...libraryItemData, author: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Publisher</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g. IVP Books"
                  value={libraryItemData.publisher || ""}
                  onChange={(e) => setLibraryItemData({ ...libraryItemData, publisher: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Media Type</label>
                <select
                  className="select-input"
                  value={libraryItemData.media_type || "ebook"}
                  onChange={(e) => setLibraryItemData({ ...libraryItemData, media_type: e.target.value })}
                >
                  <option value="ebook">eBook (Digital PDF/ePub)</option>
                  <option value="book">Physical Book</option>
                  <option value="audio">Audiobook</option>
                  <option value="article">Article</option>
                </select>
              </div>
            </div>
          </Card>

          <Card title="Digital Media File" subtitle="Upload eBook PDF or digital file.">
            {libray_id === "new" ? (
              <p className="text-xs text-gray-400 italic py-6 text-center">Save library item first before uploading media file.</p>
            ) : (
              <FileUploadField
                max_files={1}
                max_file_size={10}
                text={"Upload Digital eBook File"}
                subtext={"Upload PDF or ePub file for digital borrowing"}
                upload_url={`${config.endpoint}/file/library/id/${libray_id}`}
                onChange={() => fetchLibraryItemById()}
              />
            )}
          </Card>
        </div>
      )}

      {/* TAB 3: BORROWING SETTINGS */}
      {activeTab === "borrowing" && (
        <Card title="Borrowing Rules &amp; Visibility" subtitle="Configure digital copy limits, loan permissions, and publication status.">
          <div className="space-y-6">
            <div className="input-container max-w-md">
              <label className="input-label font-semibold">Available Copy Limit</label>
              <input
                type="number"
                className="text-input"
                placeholder="e.g. 5"
                value={libraryItemData.limit_borrowing || ""}
                onChange={(e) => setLibraryItemData({ ...libraryItemData, limit_borrowing: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum number of digital loan copies allowed concurrently.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Publication Status</label>
                  <p className="text-xs text-gray-500">Visible to mobile app users in catalog.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {libraryItemData.is_published == 1 ? "Published" : "Draft"}
                  </span>
                  <Switch
                    checked={libraryItemData?.is_published == 1}
                    onChange={(checked) => setLibraryItemData({ ...libraryItemData, is_published: checked ? 1 : 0 })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Allow Borrowing</label>
                  <p className="text-xs text-gray-500">Enable members to place loan requests.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {libraryItemData.is_loanable == 1 ? "Borrowable" : "Reference Only"}
                  </span>
                  <Switch
                    checked={libraryItemData?.is_loanable == 1}
                    onChange={(checked) => setLibraryItemData({ ...libraryItemData, is_loanable: checked ? 1 : 0 })}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </AdminLayout>
  )
}